import { Head, router } from "@inertiajs/react"
import { useCallback, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import BackofficeLayout from "@/layouts/backoffice-layout"
import { backofficeUserPath, backofficeUsersPath } from "@/routes"

interface User {
  id: number
  email: string
  plan: "free" | "plus" | "pro"
  created_at: string
}

interface Props {
  users: User[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
  }
  filters: {
    search: string
    plan: string
  }
}

const planVariant = {
  free: "secondary",
  plus: "default",
  pro: "default",
} as const

export default function BackofficeUsersIndex({
  users,
  pagination,
  filters,
}: Props) {
  const [search, setSearch] = useState(filters.search)

  const applyFilters = useCallback(
    (newFilters: Partial<typeof filters>) => {
      router.get(
        backofficeUsersPath(),
        { ...filters, ...newFilters, page: 1 },
        { preserveState: true },
      )
    },
    [filters],
  )

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search })
  }

  const handlePlanChange = (plan: string) => {
    applyFilters({ plan: plan === "all" ? "" : plan })
  }

  const goToPage = (page: number) => {
    router.get(
      backofficeUsersPath(),
      { ...filters, page },
      { preserveState: true },
    )
  }

  return (
    <BackofficeLayout>
      <Head title="Users - Backoffice" />

      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <Input
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>

          <Select
            value={filters.plan || "all"}
            onValueChange={handlePlanChange}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="plus">Plus</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-muted-foreground ml-auto text-sm">
            {pagination.total_count} users
          </span>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                className="cursor-pointer"
                onClick={() => router.visit(backofficeUserPath(user.id))}
              >
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={planVariant[user.plan]}>{user.plan}</Badge>
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-muted-foreground text-center"
                >
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => goToPage(pagination.current_page - 1)}
            >
              Previous
            </Button>
            <span className="text-muted-foreground text-sm">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => goToPage(pagination.current_page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </BackofficeLayout>
  )
}
